import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Slider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  Save,
  ArrowBack,
  ExpandMore,
  Security,
  Lock,
  CheckCircle,
  RadioButtonUnchecked,
} from '@mui/icons-material';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useRole, useUpdateRole, usePermissionsGrouped, useUpdateRolePermissions } from '../../hooks/useRoles';
import { useRoles } from '../../hooks/useRoles';
import { CATEGORY_LABELS } from '../../api/types';
import type { RolePermission } from '../../api/types';

const RoleDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    parent_role_id: '',
    priority: 25,
  });
  const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(new Set());
  const [hasChanges, setHasChanges] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  const { data: roleData, isLoading, error, refetch } = useRole(id || '');
  const { data: permissionsData } = usePermissionsGrouped();
  const { data: rolesData } = useRoles({ size: 100, include_system: true });
  const updateMutation = useUpdateRole();
  const updatePermissionsMutation = useUpdateRolePermissions();

  const role = roleData?.data;

  // Initialize form data when role loads
  useEffect(() => {
    if (role) {
      setFormData({
        name: role.name,
        description: role.description || '',
        parent_role_id: role.parent_role_id || '',
        priority: role.priority,
      });

      // Set selected permissions
      const permIds = new Set<string>();
      role.permissions.forEach((rp: RolePermission) => {
        if (rp.granted) {
          permIds.add(rp.permission_id);
        }
      });
      setSelectedPermissions(permIds);
      setHasChanges(false);
    }
  }, [role]);

  const handleFormChange = (field: string, value: any) => {
    setFormData({ ...formData, [field]: value });
    setHasChanges(true);
  };

  const handlePermissionToggle = (permissionId: string) => {
    if (role?.is_system) return; // Cannot modify system roles

    const newSelected = new Set(selectedPermissions);
    if (newSelected.has(permissionId)) {
      newSelected.delete(permissionId);
    } else {
      newSelected.add(permissionId);
    }
    setSelectedPermissions(newSelected);
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!role || !id) return;

    try {
      // Update role details
      if (!role.is_system) {
        await updateMutation.mutateAsync({
          id,
          data: {
            name: formData.name,
            description: formData.description || undefined,
            parent_role_id: formData.parent_role_id || null,
            priority: formData.priority,
          },
        });
      }

      // Update permissions
      if (!role.is_system) {
        const permissions = Array.from(selectedPermissions).map((permId) => ({
          permission_id: permId,
          granted: true,
        }));
        await updatePermissionsMutation.mutateAsync({
          roleId: id,
          permissions,
        });
      }

      setSnackbar({ open: true, message: 'Role updated successfully', severity: 'success' });
      setHasChanges(false);
      refetch();
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.response?.data?.error || 'Failed to update role', severity: 'error' });
    }
  };

  // Check if permission is inherited
  const isInherited = (permissionId: string): boolean => {
    if (!role) return false;
    return role.inherited_permissions?.some((ip: RolePermission) => ip.permission_id === permissionId) || false;
  };

  // Get the source of inherited permission
  const getInheritedSource = (permissionId: string): string | null => {
    if (!role) return null;
    const inherited = role.inherited_permissions?.find((ip: RolePermission) => ip.permission_id === permissionId);
    return inherited?.source_role_name || null;
  };

  // Available parent roles (exclude self and roles that inherit from this role)
  const availableParentRoles = (rolesData?.data || []).filter((r) => {
    if (r.id === id) return false;
    if (r.parent_role_id === id) return false;
    return true;
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress sx={{ color: '#6366f1' }} />
      </Box>
    );
  }

  if (error || !role) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load role. The role may not exist.</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/management/roles')} sx={{ mt: 2 }}>
          Back to Roles
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component={RouterLink} to="/management/roles" color="inherit" sx={{ textDecoration: 'none' }}>
          Roles
        </Link>
        <Typography color="text.primary">{role.name}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/management/roles')}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {role.is_system ? <Lock sx={{ color: '#f59e0b' }} /> : <Security sx={{ color: '#6366f1' }} />}
              <Typography variant="h4" sx={{ fontWeight: 600 }}>{role.name}</Typography>
              {role.is_system && (
                <Chip label="System Role" size="small" sx={{ bgcolor: '#f59e0b', color: 'white' }} />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary">
              {role.description || 'No description'}
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<Save />}
          onClick={handleSave}
          disabled={!hasChanges || updateMutation.isPending || updatePermissionsMutation.isPending || role.is_system}
          sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}
        >
          {(updateMutation.isPending || updatePermissionsMutation.isPending) ? (
            <CircularProgress size={20} sx={{ color: 'white' }} />
          ) : (
            'Save Changes'
          )}
        </Button>
      </Box>

      {role.is_system && (
        <Alert severity="info" sx={{ mb: 3 }}>
          System roles cannot be modified. Their permissions are managed by the platform.
        </Alert>
      )}

      {/* Role Details */}
      <Paper sx={{ bgcolor: '#1e1e1e', borderRadius: 2, p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Role Details</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          <TextField
            label="Name"
            value={formData.name}
            onChange={(e) => handleFormChange('name', e.target.value)}
            fullWidth
            disabled={role.is_system}
          />

          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => handleFormChange('description', e.target.value)}
            fullWidth
            disabled={role.is_system}
          />

          <FormControl fullWidth disabled={role.is_system}>
            <InputLabel>Parent Role (Inherits From)</InputLabel>
            <Select
              value={formData.parent_role_id}
              label="Parent Role (Inherits From)"
              onChange={(e) => handleFormChange('parent_role_id', e.target.value)}
            >
              <MenuItem value="">None</MenuItem>
              {availableParentRoles.map((r) => (
                <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Priority: {formData.priority}
            </Typography>
            <Slider
              value={formData.priority}
              onChange={(_, value) => handleFormChange('priority', value)}
              min={1}
              max={999}
              disabled={role.is_system}
              marks={[
                { value: 10, label: '10' },
                { value: 50, label: '50' },
                { value: 100, label: '100' },
              ]}
            />
          </Box>
        </Box>

        <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
          <Chip label={`Scope: ${role.scope}`} variant="outlined" />
          {role.organization_name && <Chip label={`Org: ${role.organization_name}`} variant="outlined" />}
          {role.parent_role_name && <Chip label={`Inherits: ${role.parent_role_name}`} variant="outlined" color="primary" />}
        </Box>
      </Paper>

      {/* Permissions Matrix */}
      <Paper sx={{ bgcolor: '#1e1e1e', borderRadius: 2, p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Permissions</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Select which permissions this role should have. Inherited permissions from parent roles are shown in blue.
        </Typography>

        {(permissionsData?.data || []).map((group) => (
          <Accordion key={group.category} defaultExpanded sx={{ bgcolor: 'transparent', boxShadow: 'none', '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography fontWeight={500}>
                {CATEGORY_LABELS[group.category as keyof typeof CATEGORY_LABELS] || group.label}
              </Typography>
              <Chip
                label={`${group.permissions.filter((p) => selectedPermissions.has(p.id) || isInherited(p.id)).length}/${group.permissions.length}`}
                size="small"
                sx={{ ml: 2 }}
              />
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 1 }}>
                {group.permissions.map((permission) => {
                  const isSelected = selectedPermissions.has(permission.id);
                  const inherited = isInherited(permission.id);
                  const inheritedFrom = getInheritedSource(permission.id);

                  return (
                    <Tooltip
                      key={permission.id}
                      title={
                        inherited
                          ? `Inherited from ${inheritedFrom}`
                          : permission.description || permission.name
                      }
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isSelected || inherited}
                            onChange={() => handlePermissionToggle(permission.id)}
                            disabled={role.is_system || inherited}
                            icon={<RadioButtonUnchecked />}
                            checkedIcon={inherited ? <CheckCircle sx={{ color: '#6366f1' }} /> : <CheckCircle sx={{ color: '#10b981' }} />}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2">{permission.name}</Typography>
                            {inherited && (
                              <Typography variant="caption" color="primary">
                                (Inherited)
                              </Typography>
                            )}
                          </Box>
                        }
                        sx={{
                          bgcolor: inherited ? 'rgba(99, 102, 241, 0.1)' : isSelected ? 'rgba(16, 185, 129, 0.1)' : 'transparent',
                          borderRadius: 1,
                          m: 0,
                          p: 1,
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}
      </Paper>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RoleDetailPage;
